import { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import styles from './Modal.module.css';

const FOCUSABLE_SELECTORS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  closeOnOverlay?: boolean;
  size?: ModalSize;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  ariaLabel?: string;
  isPrerendering?: boolean;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, closeOnOverlay = true, size = 'md', ariaLabelledBy, ariaDescribedBy, ariaLabel, isPrerendering = false }: ModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  const getFocusable = useCallback(() => {
    if (!dialogRef.current) return [];
    return Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS));
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        const focusable = getFocusable();
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [onClose, getFocusable]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);

      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflowY = 'scroll';

      const id = requestAnimationFrame(() => {
        const focusable = getFocusable();
        if (focusable.length > 0) focusable[0].focus();
      });
      return () => {
        cancelAnimationFrame(id);
        document.removeEventListener('keydown', handleKeyDown);

        const savedTop = document.body.style.top;
        document.body.style.position = 'static';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflowY = '';
        window.scrollTo(0, parseInt(savedTop || '0') * -1);
      };
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown, getFocusable]);

  if (!isOpen && !isPrerendering) return null;

  return createPortal(
    <div
      className={clsx(styles.overlay, !isOpen && styles.hidden)}
      onClick={isOpen && closeOnOverlay ? onClose : undefined}
      aria-hidden={!isOpen}
    >
      <div
        ref={dialogRef}
        className={clsx(styles.content, styles[size])}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-label={!ariaLabelledBy ? (ariaLabel ?? 'Dialog') : undefined}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

export default Modal;